const math = require('mathjs');
const { makeSurfaceFromReading } = require('./reading-to-surface');
const { cameraRotationDetection } = require('./camera-rotation-detection');
const { feedMaskPixelTypes } = require('./feed-masks');
const { surfacesToFeedHeightMap,
  createHeightMapFromSingleDepth,
} = require('./feed-surface-analysis');
const { BinGeometry } = require('./geometry/bin-geometry');
const { FeedHeightMap } = require('./feed-height-map');
const { makeEmptyHeightMap } = require('./height-map-helpers');

/**
 * @deprecated Use readingToFeedHeightMap() instead
 * Creates a height map from a sensor reading, bin and feed mask
 * @param {SensorImage} sensorImage
 * @param {FeedMask} feedMask
 * @param {Object} bin
 * @param {Number} pan  in radians
 * @param {Number} tilt  in radians
 * @param {Number} distanceScale
 * @param {Number} resolution
 * @returns {HeightMapData}
 */
exports.readingToHeightMap = (sensorImage, feedMask, bin, { pan = null, tilt = null, distanceScale = null, resolution = 25 } = {}) => exports.readingToFeedHeightMap(sensorImage, feedMask, BinGeometry.from(bin), { pan, tilt, distanceScale, resolution }).heightMap;

/**
 * Creates a height map from a sensor reading, bin and feed mask
 * @param {SensorImage} sensorImage
 * @param {FeedMask} feedMask
 * @param {BinGeometry} binGeometry
 * @param {Number} pan  in radians
 * @param {Number} tilt  in radians
 * @param {Number} distanceScale
 * @param {Number} resolution
 * @returns {FeedHeightMap}
 */
exports.readingToFeedHeightMap = (sensorImage, feedMask, binGeometry, { pan = null, tilt = null, distanceScale = null, resolution = 25 } = {}) => {
  if (isBadFullReading(sensorImage, feedMask)) {
    return feedHeightMapForBadFullReading(sensorImage, binGeometry, resolution);
  }

  const detectedRotation = cameraRotationDetection(sensorImage, feedMask, binGeometry, {
    panRange: pan === null ? undefined : pan,
    tiltRange: tilt === null ? undefined : tilt,
    distanceScaleRange: distanceScale === null ? undefined : distanceScale,
  });

  const feedSurface = makeSurfaceFromReading(sensorImage, feedMask, { distanceScale: detectedRotation.distanceScale, pixelTypes: [feedMaskPixelTypes.FEED] });
  const binWallSurface = makeSurfaceFromReading(sensorImage, feedMask, { distanceScale: detectedRotation.distanceScale, pixelTypes: [feedMaskPixelTypes.BIN_WALL] });
  return surfacesToFeedHeightMap({ feedSurface, binWallSurface, binGeometry, pan: detectedRotation.pan, tilt: detectedRotation.tilt, resolution });
};

function isBadFullReading(reading, feedMask) {
  const isReadingOverexposed = reading.stats.emptyPixels > 0.2 * reading.width * reading.height;
  const isMostlyFullFeedMask = feedMask.flat().filter((x) => x === feedMaskPixelTypes.FEED).length > 0.85 * feedMask.length * feedMask[0].length;

  return isReadingOverexposed && isMostlyFullFeedMask;
}

function feedHeightMapForBadFullReading(sensorImage, binGeometry, resolution) {
  // Get the depths of the center 10x10 pixels which will have the most accurate depth data:
  const depths = [];
  const yMiddle = Math.round(sensorImage.height / 2);
  const xMiddle = Math.round(sensorImage.width / 2);

  for (let y = yMiddle - 5; y < yMiddle + 5; y++) {
    for (let x = xMiddle - 5; x < xMiddle + 5; x++) {
      const pixel = sensorImage.getRaw(x, y);
      if (pixel.depthValid && pixel.depth < 1000) {
        depths.push(pixel.depth);
      }
    }
  }

  const unknownDepthDefault = 50; /* mm */
  const centralDepthMm = depths.length > 0 ? math.mean(depths) : unknownDepthDefault;
  const centralDepth = centralDepthMm / 1000; /* m */

  return new FeedHeightMap(createHeightMapFromSingleDepth({ binGeometry, centralDepth, resolution, ...binGeometry.estimatedCameraAngleForBin() }), makeEmptyHeightMap(resolution), binGeometry);
}

